import React from 'react';

import { INSTANCE } from '@lumapps/instance/keys';
import { Instance } from '@lumapps/instance/types';
import { Filter } from '@lumapps/lumx-filter-and-sort/components/Filter';
import { MultipleSelectFilterProps } from '@lumapps/lumx-filter-and-sort/components/MultipleSelectFilter';
import { useFilterUIProps } from '@lumapps/lumx-filter-and-sort/hooks/useFilterUIProps';
import { useListFilter, UseListFilterOptions } from '@lumapps/lumx-filter-and-sort/hooks/useListFilter';
import { useTranslate } from '@lumapps/translations';
import { PartialExcept } from '@lumapps/utils/types/PartialBy';

import { InstancePickerField } from '../InstancePickerField';

type PartialInstance = PartialExcept<Instance, 'id' | 'name'>;

export interface InstanceMultipleFilterProps
    extends Omit<
        MultipleSelectFilterProps<Instance>,
        'options' | 'onClearSelected' | 'selectTextFieldProps' | 'title' | 'defaults'
    > {
    /** custom select text field props */
    selectTextFieldProps?: MultipleSelectFilterProps<Instance>['selectTextFieldProps'];
    /** custom title */
    title?: string;
    /** default values */
    defaults?: Partial<UseListFilterOptions<PartialInstance>>;
}

const CLASSNAME = 'instance-multiple-filter';
/**
 * Component that implements the instance filter used for the `Filters` pattern.
 *
 * @family Filters
 * @param InstanceMultipleFilterProps
 * @returns InstanceMultipleFilter
 */
export const InstanceMultipleFilter: React.FC<InstanceMultipleFilterProps> = ({
    selectTextFieldProps,
    onSelectedMultiple,
    selection,
    onChange,
    mode,
    ...props
}) => {
    const { translateKey } = useTranslate();
    const filterUIProps = useFilterUIProps({
        parentClassName: CLASSNAME,
        className: selectTextFieldProps?.className,
    });
    const title = props.title || translateKey(INSTANCE.INSTANCES_TITLE);

    return (
        <Filter title={title} {...props} hideTitle mode={mode}>
            <InstancePickerField
                selectionType="multiple"
                value={selection}
                selectTextFieldProps={{
                    ...selectTextFieldProps,
                    className: filterUIProps.className,
                    label: title,
                    labelProps: {
                        ...selectTextFieldProps?.labelProps,
                        children: title,
                        typography: filterUIProps.typography,
                    },
                }}
                onChange={(instances = []) => {
                    onChange?.(instances);
                    onSelectedMultiple?.(instances);
                }}
            />
        </Filter>
    );
};

Object.assign(InstanceMultipleFilter, { hook: useListFilter });
